import { Show, useObservable } from "@legendapp/state/react"
import { ActivityIndicator, Header, Image, List, MCIcon, ProductPickModal, Text } from "@src/components"
import { Alert } from "@src/components/ui"
import { useNumCols } from "@src/hooks"
import { ar } from "@src/i18n"
import { ENV, formatPrice, sharedStyles, trpc } from "@src/utils"
import { Link, router, useLocalSearchParams } from "expo-router"
import { Pressable, View } from "react-native"
import { SafeAreaView } from "react-native-safe-area-context"

export default function InventoryCollection() {
  const params = useLocalSearchParams<{ id: string }>()
  const id = Number.parseInt(params.id, 10)
  const numCols = Math.max(2, useNumCols(250, { maxWidth: 1400 }))
  const utils = trpc.useUtils()
  const inventoryCollection = trpc.inventoryCollections.listProducts.useQuery({ id })
  const deleteInventoryCollection = trpc.inventoryCollections.delete.useMutation()

  if (inventoryCollection.isLoading) {
    return (
      <>
        <Header title="" />
        <ActivityIndicator className="mt-6" />
      </>
    )
  }

  return (
    <>
      <Header
        title={inventoryCollection.data?.name ?? ""}
        right={
          <View className="flex-row h-full">
            <Link asChild href={`/dashboard/inventory/collections/${id}/edit`}>
              <Pressable className="h-full px-2 justify-center active:opacity-70">
                <MCIcon name="pencil" size={24} color="#60A5FA" />
              </Pressable>
            </Link>
            <ICollectionProductsPicker />
            <Pressable
              className="h-full px-2 justify-center active:opacity-70"
              onPress={() => {
                Alert.alert(ar.delete, ar.areYouSure, [
                  { text: ar.cancel, style: "cancel" },
                  {
                    text: ar.delete,
                    style: "destructive",
                    onPress: () => {
                      deleteInventoryCollection.mutate(
                        { id },
                        {
                          onSuccess: () => {
                            router.dismissTo("/dashboard/inventory/collections")
                            utils.inventoryCollections.list.invalidate()
                            utils.inventoryCollections.forProduct.invalidate()
                          },
                          onError: () => {
                            Alert.alert(ar.somethingWentWrong)
                          },
                        },
                      )
                    },
                  },
                ])
              }}
            >
              <MCIcon name="delete" size={24} color="#EF4444" />
            </Pressable>
          </View>
        }
      />
      <List
        contentContainerStyle={sharedStyles.contentContainerWide}
        data={inventoryCollection.data?.products}
        numColumns={numCols}
        key={`storage-products-${numCols}`}
        keyExtractor={(item) => item.id.toString()}
        // onEndReached={() => products.hasNextPage && products.fetchNextPage()}
        onRefresh={inventoryCollection.refetch}
        onEndReachedThreshold={0.5}
        ListEmptyComponent={
          inventoryCollection.isLoading ? <ActivityIndicator className="py-8" /> : <Text className="text-gray-500 text-center py-8">No products found in this storage place.</Text>
        }
        renderItem={({ item }) => {
          const image = item.images?.at(-1)

          return (
            <View className={`bg-white p-3 rounded mb-3 shadow-sm mx-1 flex-1 hover:shadow-xl transition-all`}>
              <Link href={`/dashboard/inventory/storage/${id}/product/${item.id}`} asChild>
                <Pressable className="active:opacity-70">
                  {image ? (
                    <Image
                      contentFit="contain"
                      cachePolicy="memory"
                      source={`${ENV.R2_URL}/${image.key}`}
                      style={{ width: "100%", height: 120 }}
                      recyclingKey={image.key}
                      transition={250}
                      loading="lazy"
                    />
                  ) : (
                    <View className="w-full h-28 bg-gray-100 rounded items-center justify-center">
                      <MCIcon name="package-variant" size={40} color="#9CA3AF" />
                    </View>
                  )}

                  <View className="mt-3">
                    <Text className="text-sm font-semibold text-gray-900 text-center leading-tight" numberOfLines={2}>
                      {item.name}
                    </Text>

                    <View className="mt-3 items-center">
                      <Text className="text-base md:text-lg font-bold text-green-600">
                        {formatPrice(item.price)} {ar.egp}
                      </Text>
                      {item.upc && <Text className="text-xs text-gray-500 mt-1">{item.upc}</Text>}
                    </View>
                  </View>
                </Pressable>
              </Link>
            </View>
          )
        }}
        ListFooterComponent={<SafeAreaView edges={["bottom"]} />}
      />
    </>
  )
}

function ICollectionProductsPicker() {
  const params = useLocalSearchParams<{ id: string }>()
  const id = Number.parseInt(params.id, 10)
  const utils = trpc.useUtils()
  const inventoryCollection = trpc.inventoryCollections.listProducts.useQuery({ id })
  const addProductsToCollection = trpc.inventoryCollections.addProductsToCollection.useMutation()

  const picker$ = useObservable(false)
  function show() {
    picker$.set(true)
  }

  function hide() {
    picker$.set(false)
  }

  function onChange(ids: number[]) {
    addProductsToCollection.mutate(
      { collectionId: id, productIds: ids },
      {
        onSuccess: () => {
          hide()
          utils.inventoryCollections.listProducts.invalidate({ id })
          ids.forEach((productId) => {
            utils.inventoryCollections.forProduct.invalidate({ productId })
          })
        },
        onError: () => {
          Alert.alert(ar.somethingWentWrong)
        },
      },
    )
  }

  return (
    <>
      <Pressable className="h-full px-2 justify-center active:opacity-70" onPress={show}>
        <MCIcon name="link" size={24} color="#60A5FA" />
      </Pressable>
      <Show if={picker$}>
        <ProductPickModal
          onlyBaseProducts
          hide={hide}
          value={inventoryCollection.data?.products.map((p) => p.id) ?? []}
          types={["LOCAL_PRODUCT", "STANDARD_PRODUCT", "WEIGHT_PRODUCT"]}
          onChange={onChange}
        />
      </Show>
    </>
  )
}
